/**
 * @description
 * Performance service for deployment analytics and metrics.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";

/**
 * Performance service for deployment analytics
 */
export class PerformanceService extends BaseAPIClient {
  /**
   * Get performance analytics
   */
  async getPerformanceAnalytics(timeRange: '7d' | '30d' | '90d' = '30d'): Promise<{
    metrics: {
      deploymentDuration: {
        average: number;
        median: number;
        p95: number;
        p99: number;
      };
      deploymentSuccessRate: number;
      rollbackRate: number;
      averageThresholdTime: number;
      resourceProvisioningTime: {
        average: number;
        median: number;
      };
      templateDeploymentTime: {
        average: number;
        median: number;
      };
    };
    trends: {
      deploymentDuration: Array<{
        timestamp: string;
        value: number;
        metadata?: Record<string, unknown>;
      }>;
      successRate: Array<{
        timestamp: string;
        value: number;
        metadata?: Record<string, unknown>;
      }>;
      resourceProvisioning: Array<{
        timestamp: string;
        value: number;
        metadata?: Record<string, unknown>;
      }>;
    };
    insights: string[];
    recommendations: string[];
  }> {
    const response = await this.request<{
      metrics: {
        deploymentDuration: {
          average: number;
          median: number;
          p95: number;
          p99: number;
        };
        deploymentSuccessRate: number;
        rollbackRate: number;
        averageThresholdTime: number;
        resourceProvisioningTime: {
          average: number;
          median: number;
        };
        templateDeploymentTime: {
          average: number;
          median: number;
        };
      };
      trends: {
        deploymentDuration: Array<{
          timestamp: string;
          value: number;
          metadata?: Record<string, unknown>;
        }>;
        successRate: Array<{
          timestamp: string;
          value: number;
          metadata?: Record<string, unknown>;
        }>;
        resourceProvisioning: Array<{
          timestamp: string;
          value: number;
          metadata?: Record<string, unknown>;
        }>;
      };
      insights: string[];
      recommendations: string[];
    }>(`/stripe/utilities/performance-analytics?timeRange=${timeRange}`, {
      method: 'GET',
    });
    return response;
  }

  /**
   * Get time-series performance metrics
   */
  async getTimeSeriesMetrics(
    metric: 'deploymentDuration' | 'successRate' | 'resourceProvisioning',
    timeRange: '7d' | '30d' | '90d' = '30d'
  ): Promise<{
    metric: string;
    timeRange: string;
    data: Array<{
      timestamp: string;
      value: number;
      metadata?: Record<string, any>;
    }>;
  }> {
    const response = await this.request<{
      metric: string;
      timeRange: string;
      data: Array<{
        timestamp: string;
        value: number;
        metadata?: Record<string, unknown>;
      }>;
    }>(`/stripe/utilities/performance-metrics/${metric}?timeRange=${timeRange}`, {
      method: 'GET',
    });
    return response;
  }

  /**
   * Store performance snapshot
   */
  async storePerformanceSnapshot(): Promise<{ success: boolean; message: string }> {
    const response = await this.request<{ success: boolean; message: string }>('/stripe/utilities/performance-snapshot', {
      method: 'POST',
    });
    return response;
  }
}

// Export singleton instance
export const performanceService = new PerformanceService();
